import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { BUSINESS } from "@/config/businessConfig";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const MobileMenu = ({ onClose }) => (
  <motion.div
    initial={{ clipPath: "inset(0 0 100% 0)" }}
    animate={{ clipPath: "inset(0 0 0% 0)" }}
    exit={{ clipPath: "inset(0 0 100% 0)" }}
    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    className="fixed inset-0 z-40 bg-[var(--ink)] blueprint pt-28 px-6 lg:hidden"
    data-testid="mobile-menu"
  >
    <nav className="flex flex-col gap-2">
      {NAV.map((n, i) => (
        <Link key={n.to} to={n.to} onClick={onClose} data-testid={`mobile-nav-${n.label.toLowerCase()}`}
          className="font-display text-6xl font-black uppercase border-b border-[var(--line)] py-3 flex items-center justify-between">
          <span><span className="font-mono-x text-xs text-[var(--orange)] mr-3 align-top">0{i + 1}</span>{n.label}</span>
          <ArrowUpRight className="w-7 h-7 text-[var(--orange)]" />
        </Link>
      ))}
    </nav>
    <div className="mt-10 flex flex-col gap-3">
      <Link to="/contact" onClick={onClose} className="btn-orange justify-center py-4 text-lg" data-testid="mobile-quote-btn"><span>Get a Free Quote</span></Link>
      <a href={BUSINESS.phoneHref} className="btn-ghost justify-center py-4 text-lg" data-testid="mobile-call-btn"><Phone className="w-4 h-4" />{BUSINESS.phoneDisplay}</a>
    </div>
  </motion.div>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <>
      <header data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 border-b ${scrolled || open ? "bg-[rgba(15,16,18,0.82)] backdrop-blur-xl border-[var(--line)] py-3" : "bg-transparent border-transparent py-5"}`}>
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between gap-6">
          <Logo />
          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `relative font-display text-[17px] font-bold uppercase tracking-wider transition-colors duration-300 hover:text-[var(--orange)] ${isActive ? "text-[var(--orange)]" : "text-zinc-200"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-5">
            <a href={BUSINESS.phoneHref} data-testid="header-phone" className="flex items-center gap-2 font-mono-x text-sm text-zinc-300 hover:text-white transition-colors">
              <Phone className="w-4 h-4 text-[var(--orange)]" />{BUSINESS.phoneDisplay}
            </a>
            <Link to="/contact" data-testid="header-quote-btn" className="btn-orange px-5 py-3 text-base"><span>Get a Free Quote</span><ArrowUpRight className="w-4 h-4" /></Link>
          </div>
          <button onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="Toggle menu"
            className="lg:hidden w-11 h-11 grid place-items-center bg-[var(--orange)] text-[var(--ink)]">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
